import * as React from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';
import { GSStore, mergeAllTranslations, mergeFetchedUrls, resolveLangApp } from './GlobalStateStore';
import JSONController from './JSONController';
import { TranslationsContext } from './TranslationsContext';
import TranslationsStatistics from './TranslationsStatistics';
import { translateByApp, resolveStatisticsUrl, translateOrEmpty, resolveGlobalTranslationsUrl } from './TranslationsUtil';
export const GLOBAL_APP_NAME = 'global';
export const TranslationsProvider = (props) => {
    mergeAllTranslations(props.lang, props.translations, props.isTestEnvironment);
    const stateValuesFromProps = React.useMemo(() => {
        return {
            allTranslations: props.translations ? { [props.lang]: props.translations } : {},
            fetchedLangApps: props.translations ? Object.keys(props.translations) : [],
            isDevEnvironment: !!props.isTestEnvironment
        };
    }, [props.lang, props.translations, props.isTestEnvironment]);
    const globalState = useSyncExternalStore(GSStore.subscribe, GSStore.read, () => stateValuesFromProps);
    const [currentLang, setCurrentLang] = React.useState(props.lang);
    const [previousLang, setPreviousLang] = React.useState(props.lang);
    const translationStatistics = React.useMemo(() => new TranslationsStatistics(), []);
    const controller = React.useMemo(() => new JSONController(), []);
    const globalsController = React.useMemo(() => new JSONController(), []);
    React.useEffect(() => {
        const langChanged = () => {
            return currentLang !== props.lang;
        };
        const hasTranslationsForApp = (lang, app) => {
            var _a;
            return !!Object.keys(((_a = GSStore.read().allTranslations[lang]) === null || _a === void 0 ? void 0 : _a[app]) || {}).length || GSStore.read().fetchedLangApps.includes(resolveLangApp(lang, app));
        };
        const hasTranslations = (lang, app, apps) => {
            if (apps === null || apps === void 0 ? void 0 : apps.length) {
                return apps.every((appName) => hasTranslationsForApp(lang, appName));
            }
            return hasTranslationsForApp(lang, app);
        };
        const fetchTranslations = async () => {
            if (!props.disableFetch && !hasTranslations(props.lang, props.defaultApp, props.apps)) {
                mergeFetchedUrls(props.lang, props.defaultApp, props.apps);
                return await controller.fetch(props.labelsUrl);
            }
            return Promise.resolve(null);
        };
        const fetchGlobalTranslations = async () => {
            if ((!hasTranslations(props.lang, GLOBAL_APP_NAME) || langChanged()) && !props.disableFetch && props.includeGlobalTranslations !== false) {
                setCurrentLang(props.lang);
                return await globalsController.fetch(resolveGlobalTranslationsUrl(props.tier, props.lang));
            }
            return Promise.resolve(null);
        };
        const handleTranslationsResponse = (response) => {
            if (response === null || response === void 0 ? void 0 : response.translations) {
                mergeAllTranslations(props.lang, response.translations, props.isTestEnvironment);
            }
        };
        Promise.all([fetchTranslations().then(handleTranslationsResponse), fetchGlobalTranslations().then(handleTranslationsResponse)]).catch(() => {
        });
    }, [
        props.labelsUrl,
        props.tier,
        props.lang,
        props.defaultApp,
        props.apps,
        props.translations,
        props.disableFetch,
        props.includeGlobalTranslations,
        props.isTestEnvironment,
        globalState,
        controller,
        globalsController,
        currentLang
    ]);
    React.useEffect(() => {
        if (props.disableStatistics) {
            translationStatistics.stopStatistics();
        }
        else {
            translationStatistics.startStatistics(resolveStatisticsUrl(props.tier));
        }
    }, [props.disableStatistics, props.tier, translationStatistics]);
    const getValue = React.useMemo(() => {
        let resolvedTranslations = globalState.allTranslations[props.lang];
        if (!Object.keys(resolvedTranslations || {}).length && !props.isTestEnvironment) {
            resolvedTranslations = globalState.allTranslations[previousLang];
        }
        else {
            setPreviousLang(props.lang);
        }
        const translateAndPushStatistics = (app, key, paramObject) => {
            translationStatistics.pushTranslation(app, key);
            return translateByApp(resolvedTranslations, app, key, paramObject, props.isTestEnvironment, props.jsxRenderer);
        };
        const translateOrEmptyAndPushStatistics = (app, key, paramObject, logMissingTranslation) => {
            translationStatistics.pushTranslation(app, key);
            return translateOrEmpty(resolvedTranslations, app, key, paramObject, logMissingTranslation, props.jsxRenderer);
        };
        return {
            translations: resolvedTranslations,
            translate: (key, paramObject) => translateAndPushStatistics(props.defaultApp, key, paramObject),
            translateGlobal: (key, paramObject) => translateAndPushStatistics(GLOBAL_APP_NAME, key, paramObject),
            translateApp: (app, key, paramObject) => translateAndPushStatistics(app, key, paramObject),
            translateOrEmpty: (app, key, paramObject, logMissingTranslation) => translateOrEmptyAndPushStatistics(app, key, paramObject, logMissingTranslation)
        };
    }, [globalState, translationStatistics, props.lang, previousLang, props.isTestEnvironment, props.jsxRenderer, props.defaultApp]);
    return React.createElement(TranslationsContext.Provider, { value: getValue }, props.children);
};
